import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { PauseIcon, PlayIcon, Volume2Icon, VolumeXIcon, XIcon } from 'lucide-react';
import { setSmoothScrollPaused } from '../hooks/useSmoothScroll';
type Rect = {
  top: number;
  left: number;
  width: number;
  height: number;
};
export type ReelOrigin = {
  /** Part of the reel currently visible in the viewport — the player's clip window starts here */
  clip: Rect;
  /** The reel's full (unclipped) frame — the video inside starts at exactly this size and position */
  frame: Rect;
  /** The reel's visible corner radius in px */
  radius: number;
  /** Viewport size at measure time */
  vw: number;
  vh: number;
};
type ShowreelPlayerProps = {
  open: boolean;
  src: string;
  poster: string;
  /** Measured by the parent at the moment of click / close, so the very first frame is already correct */
  origin: ReelOrigin | null;
  /** Playhead to continue from */
  startTime: number;
  /** The on-page reel video — its current frame is painted as a stand-in while the player seeks */
  getSourceVideo: () => HTMLVideoElement | null;
  /** Receives the playhead so the page reel can pick up where the player left off */
  onRequestClose: (currentTime: number) => void;
};

// Long, soft in-out curve: gentle lift-off, fast middle, very slow landing
const expandEase = [0.65, 0, 0.15, 1] as const;
const uiEase = [0.23, 1, 0.32, 1] as const;
const EXPAND_S = 0.95;
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End']);
export function ShowreelPlayer({
  open,
  src,
  poster,
  origin,
  startTime,
  getSourceVideo,
  onRequestClose
}: ShowreelPlayerProps) {
  const reduce = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const closingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);

  // Reset per open
  useEffect(() => {
    if (open) {
      closingRef.current = false;
      setReady(false);
      setPlaying(true);
      setMuted(false);
      setProgress(0);
    }
  }, [open]);

  // Paint the page reel's current frame before first paint, so the expansion shows the
  // identical image while the fullscreen video seeks to the same moment.
  useLayoutEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    const source = getSourceVideo();
    if (!canvas || !source || !source.videoWidth) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const s = Math.max(w / source.videoWidth, h / source.videoHeight); // object-cover
    const dw = source.videoWidth * s;
    const dh = source.videoHeight * s;
    try {
      ctx.drawImage(source, (w - dw) / 2 * dpr, (h - dh) / 2 * dpr, dw * dpr, dh * dpr);
    } catch {
      // the poster underneath still covers the frame
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // While open: pause smooth scroll and block native scrolling without touching overflow
  // (changing overflow removes the scrollbar and makes the page underneath jump).
  useEffect(() => {
    if (!open) return;
    setSmoothScrollPaused(true);
    const block = (e: Event) => e.preventDefault();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        requestClose();
        return;
      }
      const onSlider = (document.activeElement as HTMLElement | null)?.getAttribute('role') === 'slider';
      if (e.key === ' ' && document.activeElement?.tagName !== 'BUTTON') {
        e.preventDefault();
        togglePlay();
      } else if (SCROLL_KEYS.has(e.key) && !onSlider) {
        e.preventDefault();
      }
    };
    window.addEventListener('wheel', block, {
      passive: false
    });
    window.addEventListener('touchmove', block, {
      passive: false
    });
    window.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => closeRef.current?.focus({
      preventScroll: true
    }), EXPAND_S * 1000);
    return () => {
      setSmoothScrollPaused(false);
      window.removeEventListener('wheel', block);
      window.removeEventListener('touchmove', block);
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    onRequestClose(videoRef.current?.currentTime ?? startTime);
  };
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => undefined);
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    v.currentTime = (e.clientX - rect.left) / rect.width * v.duration;
  };
  const startPlayback = (v: HTMLVideoElement) => {
    v.play().catch(() => {
      // Autoplay with sound blocked — fall back to muted playback
      v.muted = true;
      setMuted(true);
      v.play().catch(() => setPlaying(false));
    });
  };
  const vw = origin?.vw ?? window.innerWidth;
  const vh = origin?.vh ?? window.innerHeight;
  const o = origin ?? {
    clip: {
      top: 0,
      left: 0,
      width: vw,
      height: vh
    },
    frame: {
      top: 0,
      left: 0,
      width: vw,
      height: vh
    },
    radius: 0
  };

  // Window: clip-path grows from the reel's visible area to the whole screen (GPU-friendly, no layout work)
  const r = (n: number) => Math.round(n * 10) / 10;
  const clipFrom = `inset(${r(o.clip.top)}px ${r(vw - o.clip.left - o.clip.width)}px ${r(vh - o.clip.top - o.clip.height)}px ${r(o.clip.left)}px round ${r(o.radius)}px)`;
  const clipTo = 'inset(0px 0px 0px 0px round 0px)';
  // Content: starts at the reel's exact size/position, so frame one is pixel-identical to the page
  const stageFrom = {
    x: o.frame.left,
    y: o.frame.top,
    scale: o.frame.width / vw
  };
  const stageTo = {
    x: 0,
    y: 0,
    scale: 1
  };
  const transition = {
    duration: reduce ? 0.2 : EXPAND_S,
    ease: expandEase
  };
  return createPortal(<AnimatePresence data-id="element-344" data-mp="tqr5oew25b52">
      {open && <motion.div key="showreel" role="dialog" aria-modal="true" aria-label="Showreel" data-lenis-prevent className="fixed inset-0 z-[200] overflow-hidden" initial={reduce ? {
      opacity: 0,
      clipPath: clipTo
    } : {
      opacity: 1,
      clipPath: clipFrom
    }} animate={{
      opacity: 1,
      clipPath: clipTo
    }} exit={reduce ? {
      opacity: 0,
      clipPath: clipTo
    } : {
      opacity: 1,
      clipPath: clipFrom
    }} transition={transition} style={{
      willChange: 'clip-path'
    }} data-id="element-345" data-mp="tppso4vheekp">
          <motion.div className="absolute left-0 top-0 bg-black" style={{
        width: vw,
        height: vh,
        transformOrigin: '0 0',
        willChange: 'transform'
      }} initial={reduce ? stageTo : stageFrom} animate={stageTo} exit={reduce ? stageTo : stageFrom} transition={transition} data-id="element-346" data-mp="27pug7acwg9gq">
            {/* Stand-in frame: poster, then the page reel's exact current frame on top */}
            <img src={poster} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" data-id="element-347" data-mp="2rcs4zlowt9pn" />
            <canvas ref={canvasRef} aria-hidden="true" className="absolute inset-0 h-full w-full" data-id="element-348" data-mp="2vhhwkycjc24r" />

            <video ref={videoRef} src={src} playsInline preload="auto" onLoadedMetadata={e => {
          const v = e.currentTarget;
          const target = Math.min(startTime, Math.max(0, (v.duration || 0) - 0.1));
          if (target > 0.05) v.currentTime = target;else startPlayback(v);
        }} onSeeked={e => {
          // Only the initial sync should auto-start playback
          const v = e.currentTarget;
          if (v.paused && !ready && playing) startPlayback(v);
        }} onPlaying={() => setReady(true)} onClick={togglePlay} onTimeUpdate={e => {
          const v = e.currentTarget;
          if (v.duration) setProgress(v.currentTime / v.duration);
        }} onEnded={requestClose} className={`absolute inset-0 h-full w-full cursor-pointer object-cover transition-opacity duration-150 ease-out ${ready ? 'opacity-100' : 'opacity-0'}`} data-id="element-349" data-mp="30f1afx5bmcw6" />
          </motion.div>

          {/* Controls fade in once the expansion has landed, and out before it shrinks */}
          <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0,
        transition: {
          delay: reduce ? 0 : EXPAND_S * 0.85,
          duration: 0.25,
          ease: uiEase
        }
      }} exit={{
        opacity: 0,
        y: 8,
        transition: {
          duration: 0.15,
          ease: uiEase
        }
      }} className="pointer-events-none absolute inset-0" data-id="element-350" data-mp="ku78rt1p0dy1">
            <button ref={closeRef} type="button" onClick={requestClose} className="pointer-events-auto absolute right-6 top-6 inline-flex items-center gap-2 rounded-control bg-white px-4 py-2.5 text-[13px] font-medium uppercase tracking-[0.04em] text-black transition-colors duration-150 hover:bg-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black md:right-10 md:top-8" data-id="element-351" data-mp="1if3jbuucscwh">
              Close
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-352" data-mp="24t1zht8bjg90" />
            </button>

            <div className="pointer-events-auto absolute inset-x-6 bottom-6 flex items-center gap-4 text-white md:inset-x-10 md:bottom-8" data-id="element-353" data-mp="1r1dkni6oqdoc">
              <button type="button" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-chip bg-white/15 backdrop-blur-md transition-colors duration-150 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-354" data-mp="1v1e9zxvbo9a6">
                {playing ? <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-355" data-mp="1c44rcixtmztz" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-356" data-mp="32fr84lxj0r3u" />}
              </button>

              <div role="slider" aria-label="Seek" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)} tabIndex={0} onClick={seek} onKeyDown={e => {
            const v = videoRef.current;
            if (!v) return;
            if (e.key === 'ArrowRight') v.currentTime = Math.min(v.duration, v.currentTime + 5);
            if (e.key === 'ArrowLeft') v.currentTime = Math.max(0, v.currentTime - 5);
          }} className="group relative h-6 flex-1 cursor-pointer focus:outline-none" data-id="element-357" data-mp="36hm8no7mv0gk">
                <div className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 bg-white/25 transition-[height] duration-150 group-hover:h-1" data-id="element-358" data-mp="1njpx3zsoy3lm" />
                <div className="absolute left-0 top-1/2 h-[2px] -translate-y-1/2 bg-white transition-[height] duration-150 group-hover:h-1" style={{
              width: `${progress * 100}%`
            }} data-id="element-359" data-mp="1ikwgvzm0ryhr" />
              </div>

              <button type="button" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-chip bg-white/15 backdrop-blur-md transition-colors duration-150 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-360" data-mp="1o8v19ckjd2tv">
                {muted ? <VolumeXIcon className="h-4 w-4" aria-hidden="true" data-id="element-361" data-mp="2766231ltg4bf" /> : <Volume2Icon className="h-4 w-4" aria-hidden="true" data-id="element-362" data-mp="275d0ef1ps505" />}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}